import { useCallback } from 'react';

import { generateMap } from '../map/generateMap';
import {
  isFloorMoodEnabledForFloor,
  pickFloorMood,
  rollFloorMoodMapSize,
  getFloorMoodTorchDuration,
  getFloorMoodGenerationProfile,
} from '../map/floorMoods';
import {
  ensureSelectedSpecialBossFloorMap,
  getSpecialBossFloorEncounter,
  getSpecialBossFloorMood,
  getSpecialBossFloorPlayerSpawn,
  isSpecialBossFloor,
} from '../boss/specialBossFloorConfig';
import { buildBarredDoorKeysForFloor } from '../map/barredDoors';
import { maybePlaceRansackedChest } from '../map/ransackedChest';
import {
  isCorruptionEnabledForMood,
  getCorruptionConfig,
  buildInitialCorruptionState,
} from '../map/corruptionSystem';

function getFloorMoodConfig(nextFloor, { getMapSizeForFloor, runSeed, maxTorchDuration }) {
  const specialBossFloorSelection = getSpecialBossFloorEncounter(null, {
    floor: nextFloor,
    runSeed,
  });

  if (isSpecialBossFloor(nextFloor) && specialBossFloorSelection) {
    const specialBossMood = getSpecialBossFloorMood(specialBossFloorSelection);

    return {
      mood: specialBossMood,
      size: specialBossMood?.mapSize?.value || getMapSizeForFloor(nextFloor),
      torchDuration: getFloorMoodTorchDuration(specialBossMood, maxTorchDuration),
      specialBossFloorSelection,
    };
  }

  // Floor 1 remains fixed and outside the mood system.
  if (!isFloorMoodEnabledForFloor(nextFloor)) {
    const fallbackSize = getMapSizeForFloor(nextFloor);
    return {
      mood: null,
      size: fallbackSize,
      torchDuration: maxTorchDuration,
      specialBossFloorSelection: null,
    };
  }

  const pickedMood = pickFloorMood({
    floor: nextFloor,
    runSeed,
  });

  const rolledSize = rollFloorMoodMapSize(pickedMood, {
    floor: nextFloor,
    runSeed,
  });

  const torchDuration = getFloorMoodTorchDuration(
    pickedMood,
    maxTorchDuration
  );

  return {
    mood: pickedMood,
    size: rolledSize,
    torchDuration,
    specialBossFloorSelection: null,
  };
}

function buildFloorBundle({
  floor,
  runSeed,
  mood,
  size,
  spawn,
  ransackedChestSpawnedRef,
  specialBossFloorSelection = null,
}) {
  const baseMap = generateMap({
    width: size,
    height: size,
    playerStartX: spawn.x,
    playerStartY: spawn.y,
    seed: runSeed,
    floor,
    moodProfile: mood ? getFloorMoodGenerationProfile(mood) : null,
    moodId: mood?.id ?? null,
  });

  const map = ensureSelectedSpecialBossFloorMap({
    map: maybePlaceRansackedChest(baseMap, {
      spawn,
      floor,
      runSeed,
      spawnedRef: ransackedChestSpawnedRef,
    }),
    selection: specialBossFloorSelection,
    playerPos: spawn,
    runSeed,
  });

  const barredDoorKeys = buildBarredDoorKeysForFloor(map, {
    spawn,
    floor,
    runSeed,
  });

  return {
    map,
    barredDoorKeys,
  };
}

export function createInitialFloorState({
  floorLevel,
  runSeed,
  maxTorchDuration,
  getMapSizeForFloor,
  getSpawnForSize,
  ransackedChestSpawnedRef,
  specialBossFloorSelection = null,
}) {
  const floorConfig = getFloorMoodConfig(floorLevel, {
    getMapSizeForFloor,
    runSeed,
    maxTorchDuration,
  });

  const playerPos =
    getSpecialBossFloorPlayerSpawn(floorConfig.specialBossFloorSelection) ||
    getSpawnForSize(floorConfig.size);

  const floorBundle = buildFloorBundle({
    floor: floorLevel,
    runSeed,
    mood: floorConfig.mood,
    size: floorConfig.size,
    spawn: playerPos,
    ransackedChestSpawnedRef,
    specialBossFloorSelection: floorConfig.specialBossFloorSelection,
  });

  const floorCorruption = isCorruptionEnabledForMood(floorConfig.mood)
    ? buildInitialCorruptionState({
        map: floorBundle.map,
        floor: floorLevel,
        runSeed,
        spawn: playerPos,
        config: getCorruptionConfig(floorConfig.mood),
      })
    : null;

    return {
    ...floorConfig,
    playerPos,
    ...floorBundle,
    turns: floorConfig.torchDuration,
    floorCorruption,
    specialBossFloorSelection: floorConfig.specialBossFloorSelection ?? null,
  };
}

function buildNextFloorState({
  nextFloor,
  runSeed,
  currentFloorTorchDuration,
  turns,
  maxTorchDuration,
  getMapSizeForFloor,
  getSpawnForSize,
  ransackedChestSpawnedRef,
  specialBossFloorSelection = null,
}) {
  const floorConfig = getFloorMoodConfig(nextFloor, {
    getMapSizeForFloor,
    runSeed,
    maxTorchDuration,
  });

  const playerPos =
    getSpecialBossFloorPlayerSpawn(floorConfig.specialBossFloorSelection) ||
    getSpawnForSize(floorConfig.size);

  const previousTorchDuration = currentFloorTorchDuration;
  const previousRemainingTorchTurns = Math.max(0, previousTorchDuration - turns);
  const carriedRemainingTorchTurns = Math.min(
    previousRemainingTorchTurns,
    floorConfig.torchDuration
  );
  const nextTurnsValue = Math.max(
    0,
    floorConfig.torchDuration - carriedRemainingTorchTurns
  );

  const floorBundle = buildFloorBundle({
    floor: nextFloor,
    runSeed,
    mood: floorConfig.mood,
    size: floorConfig.size,
    spawn: playerPos,
    ransackedChestSpawnedRef,
    specialBossFloorSelection: floorConfig.specialBossFloorSelection,
  });

  const floorCorruption = isCorruptionEnabledForMood(floorConfig.mood)
    ? buildInitialCorruptionState({
        map: floorBundle.map,
        floor: nextFloor,
        runSeed,
        spawn: playerPos,
        config: getCorruptionConfig(floorConfig.mood),
      })
    : null;

  return {
    nextFloor,
    ...floorConfig,
    playerPos,
    ...floorBundle,
    turns: nextTurnsValue,
    floorCorruption,
    specialBossFloorSelection: floorConfig.specialBossFloorSelection ?? null,
  };
}

export function useFloorTransition({
  floorLevel,
  currentFloorTorchDuration,
  turns,
  runSeedRef,
  ransackedChestSpawnedRef,
  getMapSizeForFloor,
  getSpawnForSize,
  maxTorchDuration,
  playStepsDownSfx,
  maybeDrawTarot,

  setFloorLevel,
  setMap,
  setBarredDoorKeys,
  setPlayerPos,
  setCurrentFloorMood,
  setSpecialBossFloorSelection,
  setCurrentFloorTorchDuration,
  setTurns,
  setFloorActionCount,
  setFloorCorruption,
  setCorruptionBannerText,
  setPendingCorruptionBonusReward,
  setExploredTiles,
  setIsMiniMapOpen,
  setBrazierQuestCompletionTriggered,
  setBrazierCompletionBannerText,
  setSlainEnemiesThisFloor,
  setCleansingQuestCompletionTriggered,
  setCleansingCompletionBannerText,
  setCanRest,
  setRelics,
  setMerchantStock,
  setActiveCombatMods,
}) {
  const descendStairs = useCallback(() => {
    playStepsDownSfx();

    const nextFloor = floorLevel + 1;

    const nextFloorState = buildNextFloorState({
      nextFloor,
      runSeed: runSeedRef.current,
      currentFloorTorchDuration,
      turns,
      maxTorchDuration,
      getMapSizeForFloor,
      getSpawnForSize,
      ransackedChestSpawnedRef,
    });

    setFloorLevel(nextFloorState.nextFloor);

    setMap(nextFloorState.map);
    setBarredDoorKeys(nextFloorState.barredDoorKeys);
    setPlayerPos(nextFloorState.playerPos);
    setCurrentFloorMood(nextFloorState.mood);
    setSpecialBossFloorSelection(nextFloorState.specialBossFloorSelection ?? null);
    setCurrentFloorTorchDuration(nextFloorState.torchDuration);
    setTurns(nextFloorState.turns);

    setFloorActionCount(0);
    setFloorCorruption(nextFloorState.floorCorruption);
    setCorruptionBannerText('');
    setPendingCorruptionBonusReward(false);

    setExploredTiles(
      new Set([`${nextFloorState.playerPos.x},${nextFloorState.playerPos.y}`])
    );
    setIsMiniMapOpen(false);

    setBrazierQuestCompletionTriggered(false);
    setBrazierCompletionBannerText('');

    setSlainEnemiesThisFloor(0);
    setCleansingQuestCompletionTriggered(false);
    setCleansingCompletionBannerText('');

    // Keep the existing floor reset behavior.
    setCanRest(false);
    setRelics([]);

    // Trigger tarot draw for the next floor (and reset previous effects).
    maybeDrawTarot(nextFloorState.nextFloor);

    // Regenerate merchant stock on new floors.
    setMerchantStock(null);

    // Owned relics persist across floors.
    setActiveCombatMods(null);
  }, [
    floorLevel,
    currentFloorTorchDuration,
    turns,
    runSeedRef,
    ransackedChestSpawnedRef,
    getMapSizeForFloor,
    getSpawnForSize,
    maxTorchDuration,
    playStepsDownSfx,
    maybeDrawTarot,
    setFloorLevel,
    setMap,
    setBarredDoorKeys,
    setPlayerPos,
    setCurrentFloorMood,
    setSpecialBossFloorSelection,
    setCurrentFloorTorchDuration,
    setTurns,
    setFloorActionCount,
    setFloorCorruption,
    setCorruptionBannerText,
    setPendingCorruptionBonusReward,
    setExploredTiles,
    setIsMiniMapOpen,
    setBrazierQuestCompletionTriggered,
    setBrazierCompletionBannerText,
    setSlainEnemiesThisFloor,
    setCleansingQuestCompletionTriggered,
    setCleansingCompletionBannerText,
    setCanRest,
    setRelics,
    setMerchantStock,
    setActiveCombatMods,
  ]);

  return {
    descendStairs,
  };
}